var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Render, Rect } from '@byted/light-render';
import { getDomContentSize } from '@byted/light-render/lib/utils/dom';
import { diff } from '@byted/light-render/lib/utils/diff';
import Eventful from '@byted/light-render/lib/utils/Eventful';
import { bind as bindResizeEvent, clear as removeResizeListener } from 'size-sensor';
import * as lodash from '../utils/lodash';
import merge from '../utils/merge';
import { getRegisteredChart } from '../chart/index';
import { getRegisteredTheme } from '../theme/index';
import { getRegisteredComponent } from '../component/index';
import { createInteraction } from '../interactions';
import { renderModel } from '../model';
import { getPadding, getSidePadding } from '../utils/styleHelper';
import normalizeToArray from '../utils/normalizeToArray';
import { chartEvents } from '../action';
import { getFacetAreas, getDefaultFacetOption } from '../facet';
import { autoHideLabels } from '../utils/textLayout';
import { getRegisteredLocale } from '../locales';
import hooks from './hooks';
var optionPreprocessors = [];
var settingEnableDirtyRect = true;
export function registerPreprocessor(fn) {
    optionPreprocessors.push(fn);
}
export function enableDirtyRect(enabled) {
    settingEnableDirtyRect = enabled;
}
/**
 * Chart class
 */
var Chart = /** @class */ (function (_super) {
    __extends(Chart, _super);
    function Chart(dom, intialOption) {
        if (intialOption === void 0) { intialOption = {}; }
        var _this = _super.call(this) || this;
        _this._seriesModels = [];
        _this._loadComponents = [];
        _this._componentModels = [];
        _this._interactions = [];
        _this._plugins = [];
        _this._autoHideLabels = [];
        _this._needUpdate = false;
        _this._isRendering = false;
        _this._isInteracting = false;
        _this._forceSize = false;
        _this.onEvent = function (type) {
            var params = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                params[_i - 1] = arguments[_i];
            }
            if (!_this._option) {
                return;
            }
            if (!type) {
                return;
            }
            var arr = type.split(':');
            var handleName = "on" + arr
                .map(function (item) { return item.replace(/^./, function (str) { return str.toUpperCase(); }); })
                .join('');
            var handle = _this._option.events[handleName];
            if (lodash.isFunction(handle)) {
                handle.apply(null, params);
            }
        };
        _this._onResize = function () {
            if (_this._forceSize || !_this._dom) {
                return;
            }
            var _a = getDomContentSize(_this._dom), width = _a[0], height = _a[1];
            if (width === 0 && height === 0) {
                // parent element display:none
                return;
            }
            if (!_this._initialOption.width) {
                _this._rendererDom.style.width = "auto";
            }
            else {
                width = _this._width;
            }
            if (!_this._initialOption.height) {
                _this._rendererDom.style.height = height.toString() + "px";
            }
            else {
                height = _this._height;
            }
            if (width === _this._width && height === _this._height) {
                return;
            }
            _this._width = width;
            _this._height = height;
            _this._rendererInstance.resize(width, height);
            _this.refresh();
        };
        var domWidth;
        var domHeight;
        _this._dom = dom;
        if (typeof dom.getContext === 'function' && !_this.isBrowser()) {
            domWidth = dom.width;
            domHeight = dom.height;
        }
        else {
            var _a = getDomContentSize(dom), divWidth = _a[0], divHeight = _a[1];
            domWidth = divWidth;
            domHeight = divHeight;
        }
        var _b = intialOption.width, width = _b === void 0 ? domWidth : _b, _c = intialOption.height, height = _c === void 0 ? domHeight : _c, dpr = intialOption.dpr, _d = intialOption.renderer, renderer = _d === void 0 ? 'canvas' : _d;
        _this._width = width;
        _this._height = height;
        _this._dpr = dpr;
        _this._renderer = renderer;
        _this._initialOption = intialOption;
        _this._onResize = lodash.throttle(_this._onResize, 300).bind(_this);
        _this._initRenderer();
        _this._initEvents();
        return _this;
    }
    Chart.prototype.getDefaultOption = function () {
        return {
            background: 'none',
            animate: true,
            animateLimit: 2000,
            transition: true,
            padding: 24,
            colors: ['#4261F6', '#FFD131', '#10CECE', '#8462FF', '#6E748E', '#00A98A'],
            textStyle: {
                fontFamily: 'sans-serif',
                fontWeight: 'normal',
                fontStyle: 'normal',
            },
            data: [],
            commonOption: {},
            series: [],
            events: {},
            facet: getDefaultFacetOption(),
            plugins: [],
            interactions: [],
            slider: [],
            dataMarker: [],
        };
    };
    Chart.prototype.setOption = function (option, mergedOld) {
        var _this = this;
        if (mergedOld === void 0) { mergedOld = false; }
        if (this._disposed) {
            throw new Error('chart has been disposed!!!');
        }
        var hasInited = !!this._option;
        var refreshImmediately = !hasInited;
        this._initOption(option, mergedOld);
        var _a = this._initComponentModels(mergedOld), addedComponents = _a.addedComponents, updatedComponents = _a.updatedComponents;
        var _b = this._initSeriesModels(mergedOld), addedSeries = _b.addedSeries, updatedSeries = _b.updatedSeries;
        updatedComponents.forEach(function (model) { return model.updated(); });
        updatedSeries.forEach(function (model) { return model.updated(); });
        addedComponents.forEach(function (componentModel) { return componentModel.init(); });
        addedSeries.forEach(function (seriesModel) { return seriesModel.init(); });
        this._interactions.forEach(function (interaction) { return interaction.destroy(); });
        this._plugins.forEach(function (plugin) { return plugin.destroy(); });
        this._plugins = this._option.plugins;
        this._plugins.forEach(function (plugin) {
            plugin.chart = _this;
            plugin.mounted();
        });
        this._interactions = this._option.interactions.map(function (name) {
            if (lodash.isString(name)) {
                return createInteraction(_this, name);
            }
            if (name && name.type) {
                return createInteraction(_this, name.type, name);
            }
        });
        this.refresh(refreshImmediately);
    };
    Chart.prototype.changeData = function (data) {
        this.eachSeriesModel(function (model) {
            model.changeData(data);
        });
        this.dispatchAction({
            type: 'changedata',
        });
        this.refresh();
    };
    Chart.prototype.setInteraction = function (isInteracting) {
        this._isInteracting = isInteracting;
    };
    Chart.prototype.isInteracting = function () {
        return this._isInteracting;
    };
    Chart.prototype.getOption = function () {
        return this._option;
    };
    Chart.prototype.getDataSource = function () {
        return this._option.data;
    };
    // on mouse event and actions
    Chart.prototype.getWidth = function () {
        return this._width;
    };
    Chart.prototype.getHeight = function () {
        return this._height;
    };
    Chart.prototype.isDisposed = function () {
        return this._disposed;
    };
    Chart.prototype.getPaddingBox = function () {
        var padding = this._option.padding;
        var _a = getPadding(padding), paddingTop = _a.paddingTop, paddingLeft = _a.paddingLeft, paddingRight = _a.paddingRight, paddingBottom = _a.paddingBottom;
        var left = paddingLeft;
        var top = paddingTop;
        var right = Math.max(this._width - paddingRight, left);
        var bottom = Math.max(this._height - paddingBottom, top);
        return {
            top: top,
            left: left,
            right: right,
            bottom: bottom,
            width: Math.max(right - left, 0),
            height: Math.max(bottom - top, 0),
            center: [(left + right) / 2, (top + bottom) / 2],
        };
    };
    Chart.prototype.getFacetContentBox = function (index) {
        var _a = this._facetAreas[index || 0], x = _a.x, y = _a.y, width = _a.width, height = _a.height;
        return {
            top: y,
            left: x,
            right: x + width,
            bottom: y + height,
            width: width,
            height: height,
            center: [x + width / 2, y + height / 2],
        };
    };
    Chart.prototype.getSidePosition = function (componentId) {
        var padding = this._option.padding;
        var side = this._componentSideSize.filter(function (item) { return item.id === componentId; })[0].side;
        var sameSideComponents = this._componentSideSize.filter(function (item) { return item.side === side; });
        var position = getSidePadding(padding, side);
        for (var _i = 0, sameSideComponents_1 = sameSideComponents; _i < sameSideComponents_1.length; _i++) {
            var item = sameSideComponents_1[_i];
            if (item.id === componentId) {
                break;
            }
            position += item.size;
        }
        if (side === 'right') {
            position = this._width - position;
        }
        else if (side === 'bottom') {
            position = this._height - position;
        }
        return position;
    };
    Chart.prototype.isBrowser = function () {
        return /html.*?element/gi.test(Object.prototype.toString.call(this._dom));
    };
    Chart.prototype.getDom = function () {
        return this._dom;
    };
    Chart.prototype.getRenderInstance = function () {
        return this._rendererInstance;
    };
    Chart.prototype.getTheme = function () {
        return this._theme || {};
    };
    Chart.prototype.getLocale = function () {
        return getRegisteredLocale(this._option.locale);
    };
    Chart.prototype.addHTMLLayer = function (cssText, append) {
        if (cssText === void 0) { cssText = 'position: absolute;display: none;'; }
        if (append === void 0) { append = true; }
        if (getComputedStyle(this._dom).position === 'static') {
            this._dom.style.position = 'relative';
        }
        var div = document.createElement('div');
        div.style.cssText = cssText;
        div.className = 'lightcharts-container';
        if (append) {
            this._dom.appendChild(div);
        }
        else {
            this._dom.prepend(div);
        }
        return div;
    };
    Chart.prototype.resize = function (width, height) {
        if (width === this._width && height === this._height) {
            return;
        }
        if (this.isBrowser() && (width === undefined || height === undefined)) {
            this._onResize();
            return;
        }
        if (this.isBrowser()) {
            this._rendererDom.style.width = width + "px";
            this._rendererDom.style.height = height + "px";
        }
        this._width = width;
        this._height = height;
        this._forceSize = true;
        this._rendererInstance.resize(width, height);
        this.refresh();
    };
    Chart.prototype.refresh = function (refreshImmediately) {
        var _this = this;
        if (refreshImmediately === void 0) { refreshImmediately = false; }
        if (this._isRendering) {
            return;
        }
        if (refreshImmediately) {
            this._renderChart();
        }
        else if (!this._needUpdate) {
            this._needUpdate = true;
            var timeout = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : setTimeout;
            timeout(function () {
                !_this._disposed && _this._renderChart();
            });
        }
    };
    Chart.prototype.dispatchAction = function (action) {
        this.dispatch(action.type, action);
    };
    Chart.prototype.getBase64 = function () {
        // todo
    };
    Chart.prototype.downloadImage = function (name) {
        this._rendererInstance.downloadImage(name || this._option.title.text || Date.now().toString());
    };
    Chart.prototype.destroy = function () {
        if (this._disposed) {
            console.warn('chart has been disposed');
            return;
        }
        this._disposed = true;
        this.dispatchAction({
            type: 'destroy',
        });
        this.eachComponent(function (model) { return model.destroy(); });
        this.eachSeriesModel(function (model) { return model.destroy(); });
        this._interactions.forEach(function (interaction) { return interaction.destroy(); });
        this._plugins.forEach(function (plugin) { return plugin.destroy(); });
        if (this._rendererDom) {
            this._rendererDom.parentNode.removeChild(this._rendererDom);
        }
        this.removeAllListeners();
        if (!this._isNodeCanvas()) {
            removeResizeListener(this._dom);
        }
        this._componentModels = [];
        this._seriesModels = [];
        this._rendererInstance.dispose();
        delete this._rendererDom;
        delete this._dom;
        delete this._allRenderObj;
        delete this._rendererInstance;
        delete this._option;
        delete this._interactions;
        delete this._plugins;
    };
    Chart.prototype.getComponentModel = function (componentName, index) {
        if (index === void 0) { index = 0; }
        return this._componentModels.filter(function (model) { return model.type === componentName; })[index];
    };
    Chart.prototype.getSeriesModel = function (seriesIndex) {
        return this._seriesModels[seriesIndex];
    };
    Chart.prototype.getAllSeries = function () {
        return this._seriesModels;
    };
    Chart.prototype.getSeriesByDependency = function (componentName) {
        return this._seriesModels.filter(function (model) {
            return lodash.includes(model.getDependentComponents(), componentName);
        });
    };
    Chart.prototype.getAllRenderObjs = function () {
        return this._allRenderObj || [];
    };
    Chart.prototype.eachSeriesModel = function (callback) {
        return this._seriesModels.forEach(callback);
    };
    Chart.prototype.eachComponent = function (callback) {
        this._componentModels.forEach(callback);
    };
    Chart.prototype.addToTopLevel = function (obj, zIndex) {
        if (zIndex === void 0) { zIndex = 0; }
        if (!this._isRendering || !obj) {
            return;
        }
        if (lodash.isUndefined(obj.attr.key)) {
            console.warn('top level element must have a key');
        }
        this._topLevelRenderObj.push({ obj: obj, zIndex: zIndex });
    };
    Chart.prototype.__autoHideLabel = function (labels) {
        var _this = this;
        labels.forEach(function (label) { return _this._autoHideLabels.push(label); });
    };
    Chart.prototype._initRenderer = function () {
        var styleText = "position: relative;width: " + this._width + "px;height:" + this._height + "px;padding:0;margin:0;overflow:hidden;border-width:0;";
        if (this._isNodeCanvas()) {
            // eslint-disable-next-line new-cap
            this._rendererInstance = new Render(this.getDom(), {
                dpr: this._dpr,
                width: this._width,
                height: this._height,
            });
        }
        else {
            this._rendererDom = this.addHTMLLayer(styleText);
            // eslint-disable-next-line new-cap
            this._rendererInstance = new Render(this._rendererDom, {
                dpr: this._dpr,
                width: this._width,
                height: this._height,
                renderer: this._renderer,
                workerEnabled: this._initialOption.workerEnabled,
            });
        }
        this._rendererInstance.enableDirtyRect = settingEnableDirtyRect;
    };
    Chart.prototype._initOption = function (userOption, mergeOld) {
        var _this = this;
        var option = __assign({}, userOption);
        optionPreprocessors.forEach(function (fn) { return fn(option); });
        Chart.dependecies.forEach(function (component) {
            var ComponentClass = getRegisteredComponent(component);
            var componentOption = lodash.get(option, component);
            if (ComponentClass && ComponentClass.multiple && componentOption) {
                lodash.set(option, component, normalizeToArray(componentOption));
            }
        });
        var oldOption = this._option;
        var themeConfig = getRegisteredTheme(option.theme) || {};
        var themeKeys = ['colors', 'background', 'textStyle', 'padding'];
        var chartThemeConfig = lodash.pick(themeConfig, themeKeys);
        var mergedOption = merge({}, mergeOld ? oldOption : this.getDefaultOption(), chartThemeConfig, option);
        var series = mergedOption.series, commonOption = mergedOption.commonOption;
        mergedOption.series = mergedOption.series.map(function (seriesOption) {
            return merge({}, commonOption.series, lodash.get(commonOption, seriesOption.type), seriesOption);
        });
        var chartTypes = series.map(function (seriesOpt) { return seriesOpt.type || 'line'; });
        var uniqChartTypes = lodash.uniq(chartTypes);
        var dependencies = Chart.dependecies.slice();
        uniqChartTypes.forEach(function (type) {
            var ChartModel = getRegisteredChart(type);
            if (!ChartModel) {
                throw new Error(type + " chart not found. you may not import it");
            }
            ChartModel.prevProcessOption(mergedOption);
            var chartDependcies = ChartModel.dependencies;
            _this._getDependencies(dependencies, chartDependcies);
        });
        dependencies.forEach(function (component) {
            var componentModel = getRegisteredComponent(component);
            if (componentModel) {
                componentModel.prevProcessOption(mergedOption);
                if (componentModel.multiple) {
                    mergedOption[component] = normalizeToArray(mergedOption[component]);
                }
            }
        });
        this._loadComponents = lodash
            .uniq(dependencies)
            .filter(function (componentName) { return !!getRegisteredComponent(componentName); });
        this._option = mergedOption;
        this._theme = themeConfig;
    };
    Chart.prototype._getDependencies = function (allDependcies, dependencies) {
        for (var _i = 0, dependencies_1 = dependencies; _i < dependencies_1.length; _i++) {
            var item = dependencies_1[_i];
            if (!lodash.includes(allDependcies, item)) {
                var componentModel = getRegisteredComponent(item);
                var childDepend = componentModel.dependencies;
                allDependcies.push(item);
                this._getDependencies(allDependcies, childDepend);
            }
        }
    };
    Chart.prototype._getSeriesKeys = function (series) {
        var nameCount = {};
        return series.map(function (seriesOption, seriesIndex) {
            var key = seriesOption.type + "." + (seriesOption.name || seriesIndex);
            if (!nameCount[key]) {
                nameCount[key] = 0;
            }
            nameCount[key]++;
            return key + '.' + nameCount[key];
        });
    };
    Chart.prototype._initSeriesModels = function (mergeOld) {
        var _this = this;
        var series = this._option.series;
        var prevSeriesModels = this._seriesModels;
        var prevSeriesKeys = this._getSeriesKeys(prevSeriesModels.map(function (seriesModel) { return seriesModel.getOption(); }));
        var currrentSeriesKeys = this._getSeriesKeys(series);
        var result = diff(prevSeriesKeys, currrentSeriesKeys, function (key) { return key; });
        var nextList = prevSeriesModels.slice();
        // remove chart
        result.removed.forEach(function (index) {
            nextList[index].destroy();
            nextList.splice(index, 1);
        });
        // reoderer exsist chart and update
        result.ordered.forEach(function (_a, i) {
            var from = _a[0], to = _a[1];
            nextList.splice(from, 1);
            var oldIndex = result.pureChanged[i][0];
            var oldSeriesModel = prevSeriesModels[oldIndex];
            nextList.splice(to, 0, oldSeriesModel);
        });
        result.maintained.forEach(function (value) {
            var oldIndex = value[0], newIndex = value[1];
            var oldSeriesModel = prevSeriesModels[oldIndex];
            oldSeriesModel.update(series[newIndex], newIndex, mergeOld);
        });
        // add new chart
        var addModels = result.added.map(function (index) {
            var seriesIndex = index;
            var seriesOption = series[seriesIndex];
            var type = seriesOption.type;
            var CurrentChartModel = getRegisteredChart(type);
            var seriesModel = new CurrentChartModel(type, series[seriesIndex], _this, seriesIndex);
            nextList.splice(index, 0, seriesModel);
            return seriesModel;
        });
        this._seriesModels = nextList;
        return {
            addedSeries: addModels,
            updatedSeries: result.maintained.map(function (value) { return prevSeriesModels[value[0]]; }),
        };
    };
    Chart.prototype._initComponentModels = function (mergeOld) {
        var _this = this;
        var prevSeriesModels = this._componentModels;
        var prevKeys = prevSeriesModels.map(function (model) {
            return model.type + '-' + model.extraInfo.index;
        });
        var currentComponent = this._loadComponents.filter(function (componentName) {
            return getRegisteredComponent(componentName);
        });
        var currentKeys = [];
        currentComponent.map(function (type) {
            var ModelClass = getRegisteredComponent(type);
            if (ModelClass.multiple) {
                var arr = normalizeToArray(lodash.get(_this._option, type));
                arr.forEach(function (item, index) { return currentKeys.push(type + '-' + index); });
            }
            else {
                currentKeys.push(type + '-' + 0);
            }
        });
        var result = diff(prevKeys, currentKeys);
        var nextList = prevSeriesModels.slice();
        result.removed.forEach(function (index) {
            nextList[index].destroy();
            nextList.splice(index, 1);
        });
        result.ordered.forEach(function (_a, i) {
            var from = _a[0], to = _a[1];
            nextList.splice(from, 1);
            var oldIndex = result.pureChanged[i][0];
            var oldSeriesModel = prevSeriesModels[oldIndex];
            nextList.splice(to, 0, oldSeriesModel);
        });
        result.maintained.forEach(function (value) {
            var oldIndex = value[0], newIndex = value[1];
            var oldSeriesModel = prevSeriesModels[oldIndex];
            oldSeriesModel.update(_this._getComponentInfo(currentKeys[newIndex])[1], mergeOld);
        });
        var addModels = result.added.map(function (index) {
            var key = currentKeys[index];
            var _a = _this._getComponentInfo(key), type = _a[0], option = _a[1], componentIndex = _a[2];
            var CurrentChartModel = getRegisteredComponent(type);
            var seriesModel = new CurrentChartModel(type, option, _this, { index: componentIndex });
            nextList.splice(index, 0, seriesModel);
            return seriesModel;
        });
        this._componentModels = nextList;
        return {
            addedComponents: addModels,
            updatedComponents: result.maintained.map(function (value) { return prevSeriesModels[value[0]]; }),
        };
    };
    Chart.prototype._getComponentInfo = function (key) {
        var _a = key.split('-'), type = _a[0], _b = _a[1], index = _b === void 0 ? 0 : _b;
        var ModelClass = getRegisteredComponent(type);
        var option = lodash.get(this._option, type);
        if (!ModelClass.multiple) {
            return [type, option, Number(index)];
        }
        return [type, normalizeToArray(option)[index], Number(index)];
    };
    Chart.prototype._beforeRender = function () {
        var _this = this;
        this.dispatchAction({
            type: 'beforelayout',
        });
        this._componentSideSize = [];
        this.eachComponent(function (componentModel) {
            var areaSize = normalizeToArray(componentModel.getSideAreaSize());
            _this._componentSideSize = _this._componentSideSize.concat(areaSize.map(function (item) {
                return __assign({ id: componentModel.id }, item);
            }));
        });
        var _a = this._getContentBox(), left = _a.left, top = _a.top, width = _a.width, height = _a.height;
        this._facetAreas = getFacetAreas(left, top, width, height, this._option.facet);
        this.eachComponent(function (componentModel) {
            return !componentModel.renderAfterSeires && componentModel.beforeRender();
        });
        this.eachSeriesModel(function (seriesModel) { return seriesModel.beforeRender(); });
        this.eachComponent(function (componentModel) {
            return componentModel.renderAfterSeires && componentModel.beforeRender();
        });
        hooks.dispatch('beforerender', this);
        this._plugins.forEach(function (plugin) { return plugin.beforeRender(); });
        this.dispatchAction({
            type: chartEvents.BEFORE_RENDER,
            chart: this,
        });
    };
    Chart.prototype._afterRender = function () {
        this.eachComponent(function (componentModel) { return componentModel.afterRender(); });
        this.eachSeriesModel(function (seriesModel) { return seriesModel.afterRender(); });
    };
    Chart.prototype._renderChart = function () {
        var _this = this;
        if (this._isRendering || this._disposed) {
            console.warn('char is rendering or has been disposed');
            return;
        }
        if (!this._option || this._width <= 0 || this._height <= 0) {
            this._needUpdate = false;
            return;
        }
        this._isRendering = true;
        this._topLevelRenderObj = [];
        this._beforeRender();
        var renderArray = [];
        var sortArray = [];
        this._componentModels.forEach(function (componentModel) {
            var renderGeom = renderModel(componentModel);
            renderArray.push(renderGeom);
            sortArray.push(componentModel.zindex);
        });
        this._seriesModels.forEach(function (seriesModel) {
            var renderGeom = renderModel(seriesModel);
            renderArray.push(renderGeom);
            var zIndex = seriesModel.getOption().zIndex;
            sortArray.push(lodash.isNumber(zIndex) ? zIndex : 1);
        });
        this._option.plugins.forEach(function (plugin) {
            plugin.chart = _this;
            renderArray.push(plugin.render());
            sortArray.push(plugin.zIndex);
        });
        var sortedGroup = renderArray.sort(function (a, b) {
            var aIndex = renderArray.indexOf(a);
            var bIndex = renderArray.indexOf(b);
            return sortArray[aIndex] - sortArray[bIndex];
        });
        sortedGroup.unshift(new Rect({
            key: 'chart-background',
            x: 0,
            y: 0,
            width: this._width,
            height: this._height,
            fill: this._option.background,
            stroke: null,
            pointerEvents: 'none',
            transitionProperty: 'none',
        }));
        this._allRenderObj = sortedGroup
            .filter(function (val) { return val; })
            .concat(this._topLevelRenderObj.sort(function (a, b) { return a.zIndex - b.zIndex; }).map(function (item) { return item.obj; }));
        autoHideLabels(this._autoHideLabels);
        this._topLevelRenderObj = [];
        this._autoHideLabels.length = 0;
        // dispath afterrender before real render
        this.dispatchAction({
            type: chartEvents.AFTER_RENDER,
            chart: this,
        });
        this._plugins.forEach(function (plugin) { return plugin.afterRender(); });
        hooks.dispatch('afterrender', this);
        this._rendererInstance.updateAll(this._allRenderObj, this._option.transition);
        if (!this._allRenderObj) {
            console.warn('Chart has been detroyed in rendering');
        }
        this._allRenderObj = [];
        this._needUpdate = false;
        this._isRendering = false;
        this._afterRender();
    };
    Chart.prototype._getKeyTree = function (nodes) {
        var _this = this;
        if (nodes === void 0) { nodes = this._allRenderObj; }
        return nodes.map(function (node) {
            return {
                key: node.attr.key,
                children: _this._getKeyTree(node.children ? node.children() : []),
            };
        });
    };
    Chart.prototype._initEvents = function () {
        var _this = this;
        if (!this._isNodeCanvas()) {
            bindResizeEvent(this._dom, this._onResize);
        }
        this._rendererInstance.eventListener = function (type, params) {
            _this.dispatch(type, params[0]);
        };
    };
    Chart.prototype._isNodeCanvas = function () {
        return typeof this._dom.getContext === 'function';
    };
    Chart.prototype._getContentBox = function () {
        var padding = this._option.padding;
        var _a = getPadding(padding), paddingTop = _a.paddingTop, paddingLeft = _a.paddingLeft, paddingRight = _a.paddingRight, paddingBottom = _a.paddingBottom;
        var leftSize = paddingLeft;
        var rightSize = paddingRight;
        var topSize = paddingTop;
        var bottomSize = paddingBottom;
        this._componentSideSize.forEach(function (item) {
            var side = item.side, size = item.size;
            if (side === 'left') {
                leftSize += size;
            }
            else if (side === 'right') {
                rightSize += size;
            }
            else if (side === 'top') {
                topSize += size;
            }
            else if (side === 'bottom') {
                bottomSize += size;
            }
        });
        var top = topSize;
        var left = leftSize;
        var right = Math.max(this._width - rightSize, left);
        var bottom = Math.max(this._height - bottomSize, top);
        var width = Math.max(this._width - leftSize - rightSize, 0);
        var height = Math.max(this._height - topSize - bottomSize, 0);
        var center = [(left + right) / 2, (top + bottom) / 2];
        return { top: top, left: left, right: right, bottom: bottom, width: width, height: height, center: center };
    };
    Chart.dependecies = [
        'title',
        'tooltip',
        'coord',
        'legend',
        'slider',
        'dataZoom',
        'brush',
        'colorLegend',
        'dataMarker',
    ];
    return Chart;
}(Eventful));
export default Chart;
